import React, { useMemo, useState } from "react";
import {
  ConfirmDialog,
  DangerButton,
  EmptyState,
  FormField,
  IconButton,
  LoadingSpinner,
  Modal,
  SearchInput,
  SecondaryButton,
  StatusBadge,
  TextArea,
  debounce,
} from "../../design-system";
import { CheckIcon, ClockIcon, XIcon } from "../../design-system/icons";
import { borders, surfaces, textColors, typography } from "../../design-system/tokens";
import {
  useAdminAffiliatePayoutEvents,
  useAdminAffiliatePayouts,
  useApproveAffiliatePayout,
  useRejectAffiliatePayout,
} from "../hooks/useAdminAffiliatePayouts";
import {
  AFFILIATE_PAYOUT_STATUSES,
  getAffiliatePayoutStatusMeta,
} from "../utils/affiliatePayoutStatusMeta";

const PAGE_SIZE = 25;

// Transition rules mirrored from the eng-00099 backend contract.
const APPROVABLE_STATUSES = ["on_hold", "pending_approval", "failed"];
const REJECTABLE_STATUSES = ["on_hold", "pending_setup", "pending_approval"];

const ACCOUNT_NOT_READY_TOOLTIP =
  "The affiliate's Stripe payout account is not ready to receive payouts yet.";

// Amounts arrive as float dollars; always render as USD currency.
const formatMoney = (amount, currency = "usd") => {
  const value = Number(amount);
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: (currency || "usd").toUpperCase(),
    }).format(Number.isFinite(value) ? value : 0);
  } catch {
    return `$${(Number.isFinite(value) ? value : 0).toFixed(2)}`;
  }
};

const formatIsoDateTime = (isoString) => {
  if (!isoString) return "—";
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return isoString;
  return d.toLocaleString();
};

const noticeStyles = {
  success:
    "bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-300",
  error:
    "bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300",
};

// ---------------------------------------------------------------------------
// Status filter chips
// ---------------------------------------------------------------------------

const StatusFilterChips = ({ statusCounts, activeStatus, onSelect }) => {
  const allCount = useMemo(
    () => Object.values(statusCounts || {}).reduce((sum, n) => sum + (Number(n) || 0), 0),
    [statusCounts],
  );

  const chips = [
    { value: "", label: "All", count: allCount },
    ...AFFILIATE_PAYOUT_STATUSES.map((status) => ({
      value: status,
      label: getAffiliatePayoutStatusMeta(status).label,
      count: Number(statusCounts?.[status]) || 0,
    })),
  ];

  return (
    <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by status">
      {chips.map(({ value, label, count }) => {
        const isActive = value === activeStatus;
        const base = `inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border transition-colors ${typography.bodySmall} focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400`;
        const active = "bg-blue-600 dark:bg-blue-500 text-white border-transparent";
        const inactive = `${surfaces.primary} ${textColors.secondary} ${borders.default} ${surfaces.hover}`;
        return (
          <button
            key={value || "all"}
            type="button"
            aria-pressed={isActive}
            onClick={() => onSelect(value)}
            className={`${base} ${isActive ? active : inactive}`}
          >
            <span>{label}</span>
            <span
              className={`px-1.5 rounded-full ${typography.caption} ${
                isActive ? "bg-white/20 text-white" : `${surfaces.tertiary} ${textColors.tertiary}`
              }`}
            >
              {count}
            </span>
          </button>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Per-row expandable status history
// ---------------------------------------------------------------------------

const PayoutStatusHistory = ({ periodId }) => {
  const eventsQuery = useAdminAffiliatePayoutEvents(periodId);
  const events = eventsQuery.data?.events || [];

  if (eventsQuery.isLoading) {
    return (
      <p className={`${typography.bodySmall} ${textColors.secondary}`}>Loading status history…</p>
    );
  }
  if (eventsQuery.error) {
    return (
      <p className={`${typography.bodySmall} text-red-600 dark:text-red-400`} role="alert">
        {eventsQuery.error?.message || "Failed to load status history."}
      </p>
    );
  }
  if (events.length === 0) {
    return <p className={`${typography.bodySmall} ${textColors.secondary}`}>No status history yet.</p>;
  }

  return (
    <div>
      <h4 className={`font-heading font-bold text-xs uppercase tracking-wider ${textColors.tertiary} mb-2`}>
        Status history
      </h4>
      <ol className="space-y-2">
        {events.map((event, index) => {
          const fromMeta = event.from_status ? getAffiliatePayoutStatusMeta(event.from_status) : null;
          const toMeta = getAffiliatePayoutStatusMeta(event.to_status);
          const actor = event.actor_username
            ? `${event.actor_type} (${event.actor_username})`
            : event.actor_type;
          return (
            <li
              key={event.id ?? `${event.created_at}-${index}`}
              className={`flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:gap-3 ${typography.bodySmall}`}
            >
              <span className={`${textColors.muted} shrink-0`}>{formatIsoDateTime(event.created_at)}</span>
              <span className={textColors.primary}>
                {fromMeta ? `${fromMeta.label} → ${toMeta.label}` : `Created as ${toMeta.label}`}
              </span>
              <span className={textColors.tertiary}>by {actor}</span>
              {event.note ? <span className={`${textColors.secondary} italic`}>“{event.note}”</span> : null}
            </li>
          );
        })}
      </ol>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const AffiliatePaymentsAdminPage = () => {
  const [statusFilter, setStatusFilter] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const [expandedPeriodId, setExpandedPeriodId] = useState(null);
  const [approveTarget, setApproveTarget] = useState(null); // row | null
  const [rejectTarget, setRejectTarget] = useState(null); // row | null
  const [rejectNote, setRejectNote] = useState("");
  const [rejectNoteError, setRejectNoteError] = useState("");
  const [notice, setNotice] = useState(null); // { type, message } | null

  const debouncedSetSearch = useMemo(
    () =>
      debounce((value) => {
        setSearch(value);
        setOffset(0);
      }, 300),
    [],
  );

  const payoutsQuery = useAdminAffiliatePayouts({
    status: statusFilter,
    search,
    limit: PAGE_SIZE,
    offset,
  });
  const approveMutation = useApproveAffiliatePayout();
  const rejectMutation = useRejectAffiliatePayout();

  const rows = payoutsQuery.data?.periods || payoutsQuery.data?.results || [];
  const statusCounts = payoutsQuery.data?.status_counts || {};
  const totalCount = Number(payoutsQuery.data?.total_count ?? rows.length) || 0;

  const handleSelectStatus = (value) => {
    setStatusFilter(value);
    setOffset(0);
  };

  const handleApproveConfirm = async () => {
    if (!approveTarget) return;
    const target = approveTarget;
    setNotice(null);
    try {
      await approveMutation.mutateAsync(target.id);
      setNotice({
        type: "success",
        message: `Payout of ${formatMoney(target.commission_amount, target.currency)} to ${
          target.affiliate?.username
        } (${target.label}) approved and queued.`,
      });
    } catch (err) {
      setNotice({ type: "error", message: err?.message || "Failed to approve payout." });
    }
  };

  const closeRejectModal = () => {
    setRejectTarget(null);
    setRejectNote("");
    setRejectNoteError("");
  };

  const handleRejectSubmit = async (e) => {
    e.preventDefault();
    if (!rejectTarget) return;
    const note = rejectNote.trim();
    if (!note) {
      setRejectNoteError("A note is required — it will be shown to the affiliate.");
      return;
    }
    setNotice(null);
    try {
      await rejectMutation.mutateAsync({ periodId: rejectTarget.id, note });
      setNotice({
        type: "success",
        message: `Payout for ${rejectTarget.affiliate?.username} (${rejectTarget.label}) rejected.`,
      });
      closeRejectModal();
    } catch (err) {
      setRejectNoteError(err?.message || "Failed to reject payout.");
    }
  };

  const isInitialLoading = payoutsQuery.isLoading;
  const hasFilters = Boolean(statusFilter || search);
  const showingFrom = totalCount === 0 ? 0 : offset + 1;
  const showingTo = Math.min(offset + rows.length, offset + PAGE_SIZE, totalCount || offset + rows.length);

  return (
    <div className="p-4 sm:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div>
          <h1 className={`${typography.h2} ${textColors.primary}`}>Affiliate Payments</h1>
          <p className={`${typography.body} ${textColors.secondary} mt-1`}>
            Review monthly affiliate commissions, approve payouts, and reject with a note.
          </p>
        </div>

        {notice && (
          <div role="status" aria-live="polite" className={`rounded-2xl px-4 py-3 ${noticeStyles[notice.type]}`}>
            <p className={`${typography.bodySmall} whitespace-pre-wrap`}>{notice.message}</p>
          </div>
        )}

        <StatusFilterChips
          statusCounts={statusCounts}
          activeStatus={statusFilter}
          onSelect={handleSelectStatus}
        />

        <div className="max-w-md">
          <SearchInput
            aria-label="Search affiliate payouts"
            placeholder="Search by username or email…"
            value={searchInput}
            onChange={(e) => {
              const value = e.target.value;
              setSearchInput(value);
              debouncedSetSearch(value);
            }}
            onClear={() => {
              setSearchInput("");
              setSearch("");
              setOffset(0);
            }}
          />
        </div>

        {isInitialLoading ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <LoadingSpinner size="lg" />
            <p className={`${typography.bodySmall} ${textColors.secondary}`}>Loading affiliate payouts…</p>
          </div>
        ) : payoutsQuery.error ? (
          <div role="alert" className={`rounded-2xl px-4 py-4 ${noticeStyles.error} space-y-3`}>
            <p className={typography.bodySmall}>
              {payoutsQuery.error?.message || "Failed to load affiliate payouts."}
            </p>
            <SecondaryButton size="sm" onClick={() => payoutsQuery.refetch()}>
              Retry
            </SecondaryButton>
          </div>
        ) : rows.length === 0 ? (
          <div className={`border rounded-2xl ${borders.glass} ${surfaces.glassCard}`}>
            <EmptyState
              title={hasFilters ? "No matching payouts" : "No affiliate payouts yet"}
              description={
                hasFilters
                  ? "Try a different status filter or search term."
                  : "Commission periods appear here once affiliates start earning."
              }
            />
          </div>
        ) : (
          <>
            <div className={`border rounded-2xl overflow-hidden ${borders.glass}`}>
              <div className={`overflow-x-auto ${surfaces.glassCard}`}>
                <table className="min-w-full text-left" aria-label="Affiliate payouts table">
                  <thead className={surfaces.glass}>
                    <tr className={`${textColors.secondary} ${typography.caption}`}>
                      <th className="px-3 py-2">Affiliate</th>
                      <th className="px-3 py-2">Month</th>
                      <th className="px-3 py-2 text-right">Gross</th>
                      <th className="px-3 py-2 text-right">New referrals</th>
                      <th className="px-3 py-2 text-right">Commission %</th>
                      <th className="px-3 py-2 text-right">Commission</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">Account</th>
                      <th className="px-3 py-2">Updated</th>
                      <th className="px-3 py-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className={surfaces.glassCard}>
                    {rows.map((row) => {
                      const meta = getAffiliatePayoutStatusMeta(row.status);
                      const accountReady = Boolean(row.account?.is_payout_ready);
                      const accountExists = Boolean(row.account?.exists);
                      const canApprove = APPROVABLE_STATUSES.includes(row.status);
                      const canReject = REJECTABLE_STATUSES.includes(row.status);
                      const isExpanded = expandedPeriodId === row.id;
                      const username = row.affiliate?.username || "—";

                      return (
                        <React.Fragment key={row.id}>
                          <tr className={`${borders.glass} border-t align-top`}>
                            <td className={`px-3 py-2 ${typography.bodySmall}`}>
                              <div className={`${textColors.primary} font-medium`}>{username}</div>
                              <div className={`${typography.caption} ${textColors.tertiary} break-all`}>
                                {row.affiliate?.email || "—"}
                              </div>
                            </td>
                            <td className={`px-3 py-2 ${typography.bodySmall} ${textColors.secondary}`}>
                              {row.label}
                            </td>
                            <td className={`px-3 py-2 text-right ${typography.bodySmall} ${textColors.secondary}`}>
                              {formatMoney(row.gross_amount, row.currency)}
                            </td>
                            <td className={`px-3 py-2 text-right ${typography.bodySmall} ${textColors.secondary}`}>
                              {row.new_referral_count}
                            </td>
                            <td className={`px-3 py-2 text-right ${typography.bodySmall} ${textColors.secondary}`}>
                              {row.commission_percent}%
                            </td>
                            <td className={`px-3 py-2 text-right ${typography.bodySmall} ${textColors.primary} font-medium`}>
                              {formatMoney(row.commission_amount, row.currency)}
                            </td>
                            <td className={`px-3 py-2 ${typography.bodySmall}`}>
                              <StatusBadge size="sm" variant={meta.tone}>
                                {meta.label}
                              </StatusBadge>
                              {row.status === "failed" && row.failure_reason ? (
                                <p className={`${typography.caption} text-red-600 dark:text-red-400 mt-1 max-w-[16rem]`}>
                                  {row.failure_reason}
                                </p>
                              ) : null}
                              {row.status === "rejected" && row.review_note ? (
                                <p className={`${typography.caption} ${textColors.tertiary} mt-1 max-w-[16rem]`}>
                                  Note: {row.review_note}
                                </p>
                              ) : null}
                            </td>
                            <td className={`px-3 py-2 ${typography.bodySmall}`}>
                              <StatusBadge
                                size="sm"
                                variant={accountReady ? "success" : accountExists ? "warning" : "neutral"}
                              >
                                {accountReady ? "Ready" : accountExists ? "Not ready" : "No account"}
                              </StatusBadge>
                            </td>
                            <td className={`px-3 py-2 ${typography.bodySmall} ${textColors.secondary} whitespace-nowrap`}>
                              {formatIsoDateTime(row.updated_at)}
                            </td>
                            <td className="px-3 py-2">
                              <div className="flex flex-nowrap items-center justify-end gap-1.5">
                                {canApprove && (
                                  <IconButton
                                    size="sm"
                                    shape="square"
                                    variant="success"
                                    disabled={!accountReady}
                                    icon={<CheckIcon size="sm" aria-hidden="true" />}
                                    title={
                                      !accountReady
                                        ? ACCOUNT_NOT_READY_TOOLTIP
                                        : `Approve & pay ${username} for ${row.label}`
                                    }
                                    aria-label={`Approve and pay ${username} for ${row.label}`}
                                    onClick={() => setApproveTarget(row)}
                                  />
                                )}
                                {canReject && (
                                  <IconButton
                                    size="sm"
                                    shape="square"
                                    variant="danger"
                                    icon={<XIcon size="sm" aria-hidden="true" />}
                                    title={`Reject payout for ${username} for ${row.label}`}
                                    aria-label={`Reject payout for ${username} for ${row.label}`}
                                    onClick={() => {
                                      setRejectTarget(row);
                                      setRejectNote("");
                                      setRejectNoteError("");
                                    }}
                                  />
                                )}
                                <IconButton
                                  size="sm"
                                  shape="square"
                                  variant={isExpanded ? "primary" : "secondary"}
                                  icon={<ClockIcon size="sm" aria-hidden="true" />}
                                  title={
                                    isExpanded
                                      ? `Hide history for ${username} ${row.label}`
                                      : `View history for ${username} ${row.label}`
                                  }
                                  aria-expanded={isExpanded}
                                  aria-label={`${isExpanded ? "Hide" : "View"} history for ${username} ${row.label}`}
                                  onClick={() => setExpandedPeriodId(isExpanded ? null : row.id)}
                                />
                              </div>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr className={`${borders.glass} border-t`}>
                              <td colSpan={10} className={`px-4 py-3 ${surfaces.secondary}`}>
                                <PayoutStatusHistory periodId={row.id} />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <p className={`${typography.bodySmall} ${textColors.tertiary}`}>
                Showing {showingFrom}–{showingTo} of {totalCount}
              </p>
              <div className="flex items-center gap-2">
                <SecondaryButton
                  size="sm"
                  disabled={offset === 0 || payoutsQuery.isFetching}
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                >
                  Previous
                </SecondaryButton>
                <SecondaryButton
                  size="sm"
                  disabled={offset + PAGE_SIZE >= totalCount || payoutsQuery.isFetching}
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                >
                  Next
                </SecondaryButton>
              </div>
            </div>
          </>
        )}

        {/* Approve confirmation */}
        <ConfirmDialog
          isOpen={!!approveTarget}
          onClose={() => setApproveTarget(null)}
          onConfirm={handleApproveConfirm}
          title="Approve & pay"
          message={
            approveTarget
              ? `Pay ${formatMoney(approveTarget.commission_amount, approveTarget.currency)} to ${
                  approveTarget.affiliate?.username
                } for ${approveTarget.label}? This queues a Stripe transfer immediately.`
              : ""
          }
          variant="success"
          confirmText="Approve & pay"
          cancelText="Cancel"
          isLoading={approveMutation.isPending}
        />

        {/* Reject modal with required note */}
        <Modal isOpen={!!rejectTarget} onClose={closeRejectModal} title="Reject payout" size="md">
          {rejectTarget && (
            <form onSubmit={handleRejectSubmit} className="space-y-4" noValidate>
              <p className={`${typography.body} ${textColors.secondary}`}>
                Reject the {formatMoney(rejectTarget.commission_amount, rejectTarget.currency)} payout to{" "}
                <span className={`${textColors.primary} font-medium`}>{rejectTarget.affiliate?.username}</span> for{" "}
                {rejectTarget.label}.
              </p>
              <FormField label="Reason" htmlFor="reject-payout-note" required>
                <TextArea
                  id="reject-payout-note"
                  value={rejectNote}
                  onChange={(e) => {
                    setRejectNote(e.target.value);
                    setRejectNoteError("");
                  }}
                  rows={4}
                  placeholder="Explain why this payout is rejected…"
                  error={rejectNoteError}
                  helperText="This note will be shown to the affiliate."
                  aria-label="Rejection note"
                />
              </FormField>
              <div className="flex items-center justify-end gap-3">
                <SecondaryButton type="button" onClick={closeRejectModal} disabled={rejectMutation.isPending}>
                  Cancel
                </SecondaryButton>
                <DangerButton type="submit" loading={rejectMutation.isPending}>
                  Reject payout
                </DangerButton>
              </div>
            </form>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default AffiliatePaymentsAdminPage;
